import React from 'react';
import { cn } from '../../lib/utils';
import { BG_DARKEN_LEVELS } from '../../constants/backgrounds';
import { STEPPED_SLIDER_CLS } from '../constants';

/**
 * Stepped slider with dot markers — used for darken, fog, and similar 6-level controls.
 *
 * @param {Object} props
 * @param {number}   props.value    — Current level index (0–5)
 * @param {Function} props.onChange  — (newIndex: number) => void
 * @param {Array}    [props.levels]  — Array of { label } objects (default: BG_DARKEN_LEVELS)
 * @param {string}   [props.label]   — Section label displayed above the slider
 * @param {string}   [props.minLabel] — Left label (default: first level's label or "None")
 * @param {string}   [props.maxLabel] — Right label (default: last level's label or "Heavy")
 */

export function DarkenSlider({
  value = 0,
  onChange,
  levels = BG_DARKEN_LEVELS,
  label,
  minLabel,
  maxLabel,
}) {
  const maxIndex = levels.length - 1;
  const effectiveMin = minLabel ?? levels[0]?.label ?? 'None';
  const effectiveMax = maxLabel ?? levels[maxIndex]?.label ?? 'Heavy';

  return (
    <div>
      {label && <div className="text-[11px] text-white/55 mb-2">{label}</div>}
      <div className="flex items-center gap-3">
        <div className="flex-1 relative py-2">
          {/* Track line — inset by half thumb width (10px) so ends align with thumb center travel range */}
          <div className="absolute left-[10px] right-[10px] top-1/2 -translate-y-1/2 h-1 bg-white/10 rounded-full" />
          {/* Dot markers — inset by 5px so dot centers align with track endpoints (dot width = 10px) */}
          <div className="absolute left-[5px] right-[5px] top-1/2 -translate-y-1/2 flex justify-between pointer-events-none">
            {levels.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'w-2.5 h-2.5 rounded-full transition-all',
                  value === i
                    ? 'bg-[#808185]'
                    : 'bg-[#3a3b3f]'
                )}
              />
            ))}
          </div>
          {/* Range input */}
          <input
            type="range"
            min="0"
            max={maxIndex}
            step="1"
            value={value}
            onChange={(e) => onChange(parseInt(e.target.value, 10))}
            className={STEPPED_SLIDER_CLS}
          />
        </div>
        <span className="text-xs text-white/70 w-16 text-right">
          {levels[value]?.label || effectiveMin}
        </span>
      </div>
      <div className="mt-1 flex justify-between text-[10px] text-white/40 px-0.5">
        <span>{effectiveMin}</span>
        <span>{effectiveMax}</span>
      </div>
    </div>
  );
}
